import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, LockKey } from "@phosphor-icons/react";
import { TEST_IDS } from "@/lib/testIds";

function formatDetail(detail) {
  if (detail == null) return "Reset failed";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail.msg ?? detail);
}

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { setUser } = useAuth();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const token = params.get("token") || "";

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing reset token in URL");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/auth/reset-password", {
        token, new_password: password,
      });
      if (data.session_token) localStorage.setItem("nk_session_token", data.session_token);
      setUser(data.user);
      toast.success("Password updated. Welcome back.");
      const isAdmin = data.user.role === "super_admin" || data.user.role === "sub_admin" || data.user.is_admin;
      navigate(isAdmin ? "/admin" : (!data.user.country || !data.user.username) ? "/profile-setup" : "/home", { replace: true });
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-nk-bg text-nk-text flex flex-col">
      <header className="border-b border-nk-line/60">
        <div className="max-w-7xl mx-auto px-6 py-5 flex items-center">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-nk-mute hover:text-nk-text">
            <ArrowLeft size={14} weight="bold" /> Back to NKognito
          </Link>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-sm bg-nk-accent/10 border border-nk-accent/30 mb-4">
              <LockKey size={22} weight="duotone" className="text-nk-accent" />
            </div>
            <p className="eyebrow mb-2">Account</p>
            <h1 className="font-display text-4xl font-medium tracking-tight">Set a new password</h1>
            <p className="text-sm text-nk-mute mt-3">8+ characters, with letters and numbers.</p>
          </div>

          {!token && (
            <div className="nk-card p-4 text-sm text-nk-bad mb-4 text-center">
              No reset token found in the URL. Request a new reset link from sign-in.
            </div>
          )}

          <form onSubmit={submit} className="nk-card p-6 space-y-3">
            <input
              data-testid={TEST_IDS.authPasswordInput}
              type="password" required minLength={8}
              placeholder="New password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-nk-bg border border-nk-line px-3 py-2.5 text-sm rounded-sm focus:border-nk-accent outline-none transition-colors"
              autoComplete="new-password"
            />
            <input
              type="password" required minLength={8}
              placeholder="Confirm new password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="w-full bg-nk-bg border border-nk-line px-3 py-2.5 text-sm rounded-sm focus:border-nk-accent outline-none transition-colors"
              autoComplete="new-password"
              data-testid="auth-confirm-password-input"
            />
            <button
              disabled={busy || !token}
              data-testid="auth-reset-submit-btn"
              className="w-full bg-nk-accent text-nk-bg font-medium px-4 py-2.5 rounded-sm hover:bg-nk-accentSoft disabled:opacity-40 transition-colors"
            >
              {busy ? "Updating..." : "Update password"}
            </button>
          </form>
        </div>
      </main>
    </div>
  );
}
